import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import { Switch, message } from "antd";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import ScreenerService from "@/services/FinEaseHomeAndAdmin/APIs/ScrennerService";
import { CreateAdvancedQueryFieldPayload } from "@/utils/types/Screener/Screener";

const AddEditQueryFieldMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEdit = !!id;
  const isViewMode = location.pathname.includes("/view/");

  const [formData, setFormData] = useState<CreateAdvancedQueryFieldPayload>({
    title: "",
    sub_title: "",
    source: "",
    field_name: "",
    aliases: [],
    is_enabled: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [aliasInput, setAliasInput] = useState("");

  const fetchFieldDetails = async (_fieldId: string) => {
    setIsLoading(true);
    try {
      const response =
        await ScreenerService.getAdvancedQueryFieldById(_fieldId);
      if (response && response.success !== false) {
        setFormData({
          title: response.data.title ?? "",
          sub_title: response.data.sub_title ?? "",
          source: response.data.source ?? "",
          field_name: response.data.field_name ?? "",
          aliases: response.data.aliases ?? [],
          is_enabled: response.data.is_enabled ?? true,
        });
      } else {
        message.warning("Failed to fetch field details");
      }
    } catch (error: any) {
      console.error(error);
      const errorData = error?.response?.data?.error ?? error?.response?.data;
      message.error(
        errorData?.message ??
          "Something went wrong while fetching field details."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if ((isEdit || isViewMode) && id) {
      fetchFieldDetails(id);
    }
  }, [id, isEdit, isViewMode]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddAlias = () => {
    if (!aliasInput.trim()) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      aliases: [...prev.aliases, aliasInput.trim()],
    }));
    setAliasInput("");
  };

  const handleRemoveAlias = (index: number) => {
    setFormData(prev => ({
      ...prev,
      aliases: prev.aliases.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isViewMode) {
      navigate(finEaseHomeNavigationRoutes.QUERY_FIELD_MASTER);
      return;
    }

    setIsLoading(true);
    try {
      if (isEdit) {
        const response: any =
          await ScreenerService.updateAdvancedQueryFieldById(id!, {
            title: formData.title,
            sub_title: formData.sub_title,
            source: formData.source,
            field_name: formData.field_name,
            aliases: formData.aliases,
            is_enabled: formData.is_enabled,
          });

        if (response && response.success !== false) {
          message.success(
            response.message ?? "Query Field updated successfully!"
          );
          navigate(finEaseHomeNavigationRoutes.QUERY_FIELD_MASTER);
        } else {
          message.warning(response.message ?? "Failed to update Query Field");
        }
      } else {
        const response: any = await ScreenerService.createAdvancedQueryField({
          title: formData.title,
          sub_title: formData.sub_title,
          source: formData.source,
          field_name: formData.field_name,
          aliases: formData.aliases,
          is_enabled: formData.is_enabled,
        });

        if (response && response.success !== false) {
          message.success(
            response.message ?? "Query Field created successfully!"
          );
          navigate(finEaseHomeNavigationRoutes.QUERY_FIELD_MASTER);
        } else {
          message.warning(response.message ?? "Failed to create Query Field");
        }
      }
    } catch (error: any) {
      console.error(error);
      const errorData = error?.response?.data?.error ?? error?.response?.data;

      if (errorData?.fields && typeof errorData.fields === "object") {
        Object.entries(errorData.fields).forEach(([field, messages]) => {
          const msgs = Array.isArray(messages)
            ? messages.join(", ")
            : String(messages);
          const formattedField = field
            .replace(/_/g, " ")
            .replace(/\b\w/g, c => c.toUpperCase());
          message.error(`${formattedField}: ${msgs}`);
        });
      } else {
        message.error(
          errorData?.message ??
            "An error occurred while saving the Query Field."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50 font-sans text-sm">
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto w-full max-w-5xl">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-5">
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() =>
                    navigate(finEaseHomeNavigationRoutes.QUERY_FIELD_MASTER)
                  }
                  className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    {isViewMode
                      ? `View Query Field`
                      : isEdit
                        ? `Edit Query Field`
                        : "Create Query Field"}
                  </h1>
                  <p className="mt-0.5 text-sm font-medium text-slate-500">
                    Configure Screener Advanced Query Field details
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {isViewMode ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() =>
                      navigate(finEaseHomeNavigationRoutes.QUERY_FIELD_MASTER)
                    }
                    className="h-10 bg-primary px-5 text-sm font-semibold text-white"
                  >
                    Back to List
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="h-11 gap-2.5 bg-primary px-8 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:bg-primary/95"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isEdit ? "Save Changes" : "Create Field"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-1 overflow-auto bg-slate-50/50">
          <div className="mx-auto w-full max-w-5xl flex-1 p-6">
            <Card className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
              <CardContent className="space-y-8 p-8">
                <div>
                  <h3 className="mb-6 border-b pb-3 text-lg font-bold text-primary">
                    Field Information
                  </h3>
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                    <div className="space-y-3">
                      <Label
                        htmlFor="title"
                        className="text-sm font-semibold text-slate-700"
                      >
                        Title
                      </Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="e.g. Market Cap"
                        disabled={isViewMode}
                        className="h-11 border-slate-200 bg-slate-50/50 transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="sub_title"
                        className="text-sm font-semibold text-slate-700"
                      >
                        Sub Title
                      </Label>
                      <Input
                        id="sub_title"
                        name="sub_title"
                        value={formData.sub_title}
                        onChange={handleInputChange}
                        placeholder="e.g. Company Market Capitalization"
                        disabled={isViewMode}
                        className="h-11 border-slate-200 bg-slate-50/50 transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="field_name"
                        className="text-sm font-semibold text-slate-700"
                      >
                        Field Name (DB Key)
                      </Label>
                      <Input
                        id="field_name"
                        name="field_name"
                        value={formData.field_name}
                        onChange={handleInputChange}
                        placeholder="e.g. mcap"
                        disabled={isViewMode}
                        className="h-11 border-slate-200 bg-slate-50/50 transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="source"
                        className="text-sm font-semibold text-slate-700"
                      >
                        Source
                      </Label>
                      <Input
                        id="source"
                        name="source"
                        value={formData.source}
                        onChange={handleInputChange}
                        placeholder="e.g. CompanyMaster"
                        disabled={isViewMode}
                        className="h-11 border-slate-200 bg-slate-50/50 transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                        required
                      />
                    </div>

                    <div className="col-span-1 space-y-3 md:col-span-2">
                      <Label className="text-sm font-semibold text-slate-700">
                        Aliases
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          value={aliasInput}
                          onChange={e => setAliasInput(e.target.value)}
                          placeholder="e.g. mcap, market_cap"
                          disabled={isViewMode}
                          className="h-11 border-slate-200 bg-slate-50/50 transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                          onKeyDown={e => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddAlias();
                            }
                          }}
                        />
                        {!isViewMode && (
                          <Button
                            type="button"
                            onClick={handleAddAlias}
                            className="h-11 bg-secondary px-6 text-white hover:bg-secondary/90"
                          >
                            Add Alias
                          </Button>
                        )}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {formData.aliases.map((alias, index) => (
                          <div
                            key={alias}
                            className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-sm text-slate-700"
                          >
                            <span>{alias}</span>
                            {!isViewMode && (
                              <button
                                type="button"
                                onClick={() => handleRemoveAlias(index)}
                                className="text-slate-400 hover:text-red-500"
                              >
                                &times;
                              </button>
                            )}
                          </div>
                        ))}
                        {formData.aliases.length === 0 && (
                          <p className="text-sm italic text-slate-400">
                            No aliases added yet.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="block text-sm font-semibold text-slate-700">
                        Status
                      </Label>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={formData.is_enabled}
                          onChange={checked =>
                            setFormData({ ...formData, is_enabled: checked })
                          }
                          disabled={isViewMode}
                          style={{
                            backgroundColor: formData.is_enabled
                              ? "#66A0CD"
                              : "#9ca3af",
                          }}
                        />
                        <span
                          className={`text-sm font-medium ${formData.is_enabled ? "text-brandPrimary" : "text-slate-500"}`}
                        >
                          {formData.is_enabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddEditQueryFieldMaster;
